import { useState, useEffect } from 'react';
import {
  calculateProductionOrderCost,
  type ProductionCostRecord,
} from '@/services/costingService';
import { getData } from '@/services/FetchNodeServices';
import { useWarehouseStore } from '@/stores/warehouseStore';

export default function CalculateCostModal({
  onClose,
  onSave,
  initialMethod = 'STANDARD',
}: {
  onClose: () => void;
  onSave: (costRecord: ProductionCostRecord) => void;
  initialMethod?: 'STANDARD' | 'ACTUAL';
}) {
  const { selectedWarehouseId } = useWarehouseStore();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [productionOrders, setProductionOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [selectedPO, setSelectedPO] = useState<any | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<'STANDARD' | 'ACTUAL'>(initialMethod);
  const [calculating, setCalculating] = useState(false);
  const [calculatedResult, setCalculatedResult] = useState<ProductionCostRecord | null>(null);
  const [calcError, setCalcError] = useState<string | null>(null);

  // Actual cost is calculated by the system when the order is completed, so it can only be recalculated after that
  const actualAllowed = String(selectedPO?.status || '').toUpperCase() === 'COMPLETED';

  const fetchPOs = async () => {
    setLoadingOrders(true);
    try {
      const url = selectedWarehouseId
        ? `api/v1/manufacturing/production-orders?warehouseId=${selectedWarehouseId}`
        : 'api/v1/manufacturing/production-orders';
      const res = await getData(url);
      if (res && res.data) {
        setProductionOrders(res.data);
      } else if (Array.isArray(res)) {
        setProductionOrders(res);
      }
    } catch (err) {
      console.error("Error fetching production orders:", err);
    } finally {
      setLoadingOrders(false);
    }
  };

  useEffect(() => {
    fetchPOs();
  }, [selectedWarehouseId]);

  const handleCalculate = async () => {
    if (!selectedPO) return;
    setCalculating(true);
    setCalcError(null);
    try {
      const res = await calculateProductionOrderCost({
        productionOrderId: selectedPO.id,
        costingMethod: selectedMethod,
        status: 'FINALIZED',
        warehouseId: selectedWarehouseId || selectedPO.warehouse_id || null,
      });

      if (res && res.success && res.data) {
        setCalculatedResult(res.data);
        setStep(3);
      } else {
        setCalcError(res?.message || 'Failed to calculate cost. Please verify production order data.');
      }
    } catch (err: any) {
      console.error("Error calculating cost:", err);
      setCalcError(err?.message || 'Server error while calculating cost.');
    } finally {
      setCalculating(false);
    }
  };

  const handleSave = () => {
    if (calculatedResult) {
      onSave(calculatedResult);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Calculate Production Cost</h2>
            <div className="flex items-center gap-2 mt-1">
              <div className={`text-xs font-medium px-2 py-0.5 rounded-full ${step === 1 ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>Step 1</div>
              <div className="text-gray-300">→</div>
              <div className={`text-xs font-medium px-2 py-0.5 rounded-full ${step === 2 ? 'bg-gray-900 text-white' : step > 2 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>Step 2</div>
              <div className="text-gray-300">→</div>
              <div className={`text-xs font-medium px-2 py-0.5 rounded-full ${step === 3 ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'}`}>Preview</div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Step 1 — Select PO */}
        {step === 1 && (
          <div className="p-6 space-y-4">
            <label className="block text-sm font-medium text-gray-700">Select Production Order</label>
            {loadingOrders ? (
              <div className="text-sm text-gray-500 p-4 text-center">Loading production orders...</div>
            ) : productionOrders.length === 0 ? (
              <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg">
                No production orders available in database.
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {productionOrders.map((po) => {
                  const active = selectedPO?.id === po.id;
                  const poNum = po.po_number || po.production_order_number || po.poNumber || 'PRD-ORDER';
                  const prodName = po.item_name || po.product_name || po.productName || 'Item';
                  const qty = po.quantity || po.planned_qty || 1;
                  return (
                    <button
                      key={po.id}
                      onClick={() => setSelectedPO(po)}
                      className={`w-full text-left p-3 rounded-lg border cursor-pointer transition-all ${
                        active ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${active ? 'text-gray-900' : 'text-gray-700'}`}>
                          {poNum} — {prodName}
                        </span>
                        <span className="text-xs text-gray-500">
                          {qty} {po.unit || 'Pcs'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Status: {po.status || '—'}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button
                onClick={() => setStep(2)}
                disabled={!selectedPO}
                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                Next: Select Method →
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Select Method */}
        {step === 2 && (
          <div className="p-6 space-y-4">
            {calcError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-2">
                <i className="ri-error-warning-line text-sm text-red-500 mt-0.5 shrink-0" />
                <span>{calcError}</span>
              </div>
            )}

            <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700">
              <span className="font-medium">Selected PO:</span> {selectedPO?.po_number || selectedPO?.production_order_number || selectedPO?.poNumber} — {selectedPO?.item_name || selectedPO?.product_name || selectedPO?.productName}
            </div>

            <label className="block text-sm font-medium text-gray-700">Select Costing Method</label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => setSelectedMethod('STANDARD')}
                className={`text-left p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedMethod === 'STANDARD' ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-sm font-semibold text-gray-900">Standard Cost</div>
                <div className="text-xs text-gray-500 mt-1">
                  Calculates planned material from BOM and routing labor rules
                </div>
              </button>

              <button
                onClick={() => setSelectedMethod('ACTUAL')}
                disabled={!actualAllowed}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  !actualAllowed
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    : selectedMethod === 'ACTUAL' ? 'border-gray-900 bg-gray-50 cursor-pointer' : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                }`}
              >
                <div className="text-sm font-semibold text-gray-900">Actual Cost</div>
                <div className="text-xs text-gray-500 mt-1">
                  {actualAllowed
                    ? 'Aggregates logged operator hours, actual material, and scrap loss'
                    : 'Calculated automatically when this production order is marked complete'}
                </div>
              </button>
            </div>

            <div className="flex justify-between pt-2">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                ← Back
              </button>
              <button
                onClick={handleCalculate}
                disabled={calculating || (selectedMethod === 'ACTUAL' && !actualAllowed)}
                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:bg-gray-400 cursor-pointer whitespace-nowrap"
              >
                {calculating ? 'Calculating in Background...' : 'Calculate Cost →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Preview */}
        {step === 3 && calculatedResult && (
          <div className="p-6 space-y-5">
            <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700">
              <span className="font-medium">{calculatedResult.productionOrderNumber}</span> — {calculatedResult.productName} — {calculatedResult.costingMethod} Cost
            </div>

            {/* Cost Breakdown Table */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Calculated Cost Breakdown</h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Category</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Amount</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Cost / Unit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="px-4 py-2 text-gray-700">Material Cost</td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900">₹{calculatedResult.materialCost.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-gray-700">₹{(calculatedResult.materialCost / (calculatedResult.producedQty || 1)).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 text-gray-700">Labour Cost</td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900">₹{calculatedResult.labourCost.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-gray-700">₹{(calculatedResult.labourCost / (calculatedResult.producedQty || 1)).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 text-gray-700">Overhead Cost</td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900">₹{calculatedResult.overheadCost.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-gray-700">₹{(calculatedResult.overheadCost / (calculatedResult.producedQty || 1)).toFixed(2)}</td>
                    </tr>
                    {calculatedResult.rejectionCost > 0 && (
                      <tr>
                        <td className="px-4 py-2 text-gray-700">Rejection Cost</td>
                        <td className="px-4 py-2 text-right font-medium text-gray-900">₹{calculatedResult.rejectionCost.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right text-gray-700">₹{(calculatedResult.rejectionCost / (calculatedResult.producedQty || 1)).toFixed(2)}</td>
                      </tr>
                    )}
                    <tr className="bg-gray-50 font-medium">
                      <td className="px-4 py-2 text-gray-900">TOTAL COST</td>
                      <td className="px-4 py-2 text-right text-gray-900">₹{calculatedResult.totalCost.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-gray-900">₹{calculatedResult.costPerUnit.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Profitability */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-emerald-800 mb-2">Margin Projection</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-xs text-emerald-600">Sale Price / Unit</div>
                  <div className="font-medium text-emerald-900">₹{calculatedResult.salePrice.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs text-emerald-600">Cost / Unit</div>
                  <div className="font-medium text-emerald-900">₹{calculatedResult.costPerUnit.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs text-emerald-600">Gross Margin %</div>
                  <div className="font-medium text-emerald-900">
                    {calculatedResult.marginPct.toFixed(1)}% (Grade {calculatedResult.grade})
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <button
                onClick={() => setStep(2)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                ← Recalculate
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-save-line mr-1" />
                Done & Save Record
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}